import { type PhysicsTuning, RUNOFF_SURFACES, SIM_DT, TORQUE_CURVE } from '../config/tuning';
import { Surface } from '../track/types';
import { approach, clamp, datan2, dcos, dsin, wrapAngle } from './dmath';
import type { InputFrame } from './input';
import { barrierAt, makeProjection, projectLocal, surfaceAt, type TrackRuntime } from './trackQuery';

/**
 * Simplified bicycle-model car. Pure data, stepped at a fixed 60 Hz with
 * substeps, using only deterministic arithmetic.
 *
 * Body frame: vx forward, vy left, yawRate counter-clockwise.
 */
export interface CarState {
  x: number;
  y: number;
  heading: number;
  vx: number;
  vy: number;
  yawRate: number;
  /** Smoothed steering, -1..1 (left positive). */
  steer: number;
  /** Front wheel angle, radians. */
  wheelAngle: number;
  gear: number;
  /** 1 while driving backwards. */
  reverse: number;
  reverseHold: number;
  /** Engine rpm (includes wheelspin flare, for audio). */
  rpm: number;
  /** Pedals as applied this tick (after the ramp), 0..1. */
  throttle: number;
  brake: number;
  /** Body-frame acceleration from tire/aero forces, m/s^2. */
  ax: number;
  ay: number;
  /** Slip angles, radians. */
  slipFront: number;
  slipRear: number;
  /** 0..1 */
  wheelspin: number;
  lockup: number;
  /** Surface under FL, FR, RL, RR. */
  surfaces: number[];
  /** Nearest centerline segment (projection hint). */
  trackIndex: number;
  /** Track position of the car's center. */
  s: number;
  d: number;
  /** Impact speed into a barrier this tick, m/s (0 = none). */
  barrierImpact: number;
  odometer: number;
}

const G = 9.81;
const RPM_PER_RAD_S = 9.549296585513721; // 60 / (2 pi)

export function createCar(): CarState {
  return {
    x: 0,
    y: 0,
    heading: 0,
    vx: 0,
    vy: 0,
    yawRate: 0,
    steer: 0,
    wheelAngle: 0,
    gear: 1,
    reverse: 0,
    reverseHold: 0,
    rpm: 0,
    throttle: 0,
    brake: 0,
    ax: 0,
    ay: 0,
    slipFront: 0,
    slipRear: 0,
    wheelspin: 0,
    lockup: 0,
    surfaces: [Surface.Asphalt, Surface.Asphalt, Surface.Asphalt, Surface.Asphalt],
    trackIndex: 0,
    s: 0,
    d: 0,
    barrierImpact: 0,
    odometer: 0,
  };
}

export function torqueFactor(rpmNorm: number): number {
  const c = TORQUE_CURVE;
  if (rpmNorm <= c[0][0]) return c[0][1];
  for (let i = 1; i < c.length; i++) {
    if (rpmNorm <= c[i][0]) {
      const t = (rpmNorm - c[i - 1][0]) / (c[i][0] - c[i - 1][0]);
      return c[i - 1][1] + (c[i][1] - c[i - 1][1]) * t;
    }
  }
  return c[c.length - 1][1];
}

/** Normalized lateral force: rises to 1 at the peak slip angle, then falls off while sliding. */
function tireShape(u: number, falloff: number): number {
  if (u < 1) return u * (1.5 - 0.5 * u * u);
  const k = (u - 1) / 1.5;
  return 1 - falloff * (k > 1 ? 1 : k);
}

function gearRatio(p: PhysicsTuning, gear: number): number {
  switch (gear) {
    case 1:
      return p.gear1;
    case 2:
      return p.gear2;
    case 3:
      return p.gear3;
    case 4:
      return p.gear4;
    default:
      return p.gear5;
  }
}

export const MAX_GEAR = 5;

/** Gear the automatic box would pick at this speed (used when spawning). */
export function gearForSpeed(p: PhysicsTuning, speed: number): number {
  for (let g = 1; g < MAX_GEAR; g++) {
    const rpm = (speed / p.rearWheelRadius) * gearRatio(p, g) * p.finalDrive * RPM_PER_RAD_S;
    if (rpm < p.shiftUpRpm * 0.93) return g;
  }
  return MAX_GEAR;
}

function surfaceGrip(p: PhysicsTuning, s: number): number {
  switch (s) {
    case Surface.Asphalt:
      return 1;
    case Surface.Curb:
      return p.curbGrip;
    case Surface.Grass:
      return p.grassGrip;
    case Surface.Turf:
      return RUNOFF_SURFACES.turfGrip;
    case Surface.Runoff:
      return RUNOFF_SURFACES.tarmacGrip;
    default:
      return p.gravelGrip;
  }
}

function surfaceDrag(p: PhysicsTuning, s: number): number {
  switch (s) {
    case Surface.Asphalt:
      return 0;
    case Surface.Curb:
      return p.curbDrag;
    case Surface.Grass:
      return p.grassDrag;
    case Surface.Turf:
      return RUNOFF_SURFACES.turfDrag;
    case Surface.Runoff:
      return RUNOFF_SURFACES.tarmacDrag;
    default:
      return p.gravelDrag;
  }
}

/**
 * Speed-sensitive lock: full lock is a margin above the steering the tightest
 * possible line at this speed needs, so keyboard players can still overdrive
 * the front tires (understeer) but can't just saw at the wheel.
 */
export function maxSteerAngle(p: PhysicsTuning, speed: number): number {
  const L = p.cgToFront + p.cgToRear;
  const v = speed > 1 ? speed : 1;
  const v2 = v * v;
  // The front's grip in g at this speed: downforce adds load (per v^2, over its static load).
  const gF = G + ((p.downforceFront * L) / (p.mass * p.cgToRear)) * v2;
  // Full lock = margin x the steering the tightest grip-limited line needs, plus a little overdrive.
  const limit = ((L * p.tireMu * gF) / v2) * p.steerLimitMargin + p.steerOverdrive;
  return limit < p.maxSteerAngle ? limit : p.maxSteerAngle;
}

const proj = makeProjection();

/** Wheel contact points in world space: FL, FR, RL, RR. */
export function wheelPositions(car: CarState, p: PhysicsTuning, out: number[]): number[] {
  const ch = dcos(car.heading);
  const sh = dsin(car.heading);
  const fx = car.x + ch * p.cgToFront;
  const fy = car.y + sh * p.cgToFront;
  const rx = car.x - ch * p.cgToRear;
  const ry = car.y - sh * p.cgToRear;
  // left = (-sin, cos)
  out[0] = fx - sh * p.halfTrackFront;
  out[1] = fy + ch * p.halfTrackFront;
  out[2] = fx + sh * p.halfTrackFront;
  out[3] = fy - ch * p.halfTrackFront;
  out[4] = rx - sh * p.halfTrackRear;
  out[5] = ry + ch * p.halfTrackRear;
  out[6] = rx + sh * p.halfTrackRear;
  out[7] = ry - ch * p.halfTrackRear;
  return out;
}

const wheelBuf = [0, 0, 0, 0, 0, 0, 0, 0];

export function stepCar(car: CarState, input: InputFrame, rt: TrackRuntime, p: PhysicsTuning): void {
  const dt = SIM_DT;
  const L = p.cgToFront + p.cgToRear;
  const m = p.mass;
  const throttleIn = input.throttle / 255;
  const brakeIn = input.brake / 255;
  const steerIn = input.steer / 127;

  // --- steering rack: rate-limited toward the input
  const outward = steerIn * car.steer >= 0 && Math.abs(steerIn) > Math.abs(car.steer);
  car.steer = approach(car.steer, steerIn, (outward ? p.steerRate : p.steerReturnRate) * dt);

  const speed0 = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
  car.wheelAngle = car.steer * maxSteerAngle(p, speed0);
  const cd = dcos(car.wheelAngle);
  const sd = dsin(car.wheelAngle);

  // --- reverse: hold the brake at a standstill; throttle returns to drive
  if (car.reverse === 0) {
    if (brakeIn > 0.5 && throttleIn < 0.1 && car.vx < 0.8 && car.vx > -0.5) car.reverseHold++;
    else car.reverseHold = 0;
    if (car.reverseHold > 30) {
      car.reverse = 1;
      car.reverseHold = 0;
    }
  } else if (throttleIn > 0.1 && car.vx > -1) {
    car.reverse = 0;
    car.gear = 1;
  }
  // Pedals ramp: brake pressure builds and the engine picks up over a few ticks.
  car.throttle = approach(car.throttle, throttleIn, (throttleIn > car.throttle ? p.pedalRise : p.pedalRelease) * dt);
  car.brake = approach(car.brake, brakeIn, (brakeIn > car.brake ? p.pedalRise : p.pedalRelease) * dt);
  const drivePedal = car.reverse ? car.brake : car.throttle;
  const brakePedal = car.reverse ? car.throttle : car.brake;

  // --- surfaces under each wheel
  const wp = wheelPositions(car, p, wheelBuf);
  let gripF = 0;
  let gripR = 0;
  let dragAcc = 0;
  for (let w = 0; w < 4; w++) {
    projectLocal(rt, wp[w * 2], wp[w * 2 + 1], car.trackIndex, 8, proj);
    const s = surfaceAt(rt, proj);
    car.surfaces[w] = s;
    if (w < 2) gripF += surfaceGrip(p, s) * 0.5;
    else gripR += surfaceGrip(p, s) * 0.5;
    dragAcc += surfaceDrag(p, s) * 0.25;
  }

  // --- hills: gravity pulls along the slope, and the road's vertical curve adds
  // load in a dip (compression) or takes it away over a crest. Flat days: no-ops.
  const gi = car.trackIndex;
  const grade = rt.grade[gi];
  const cosSlope = 1 / Math.sqrt(1 + grade * grade);
  const sinSlope = grade * cosSlope;
  const bend = rt.vcurv[gi] * cosSlope * cosSlope * cosSlope;
  const hcos = dcos(car.heading);
  const hsin = dsin(car.heading);
  const along = rt.tx[gi] * hcos + rt.ty[gi] * hsin;
  const across = -rt.tx[gi] * hsin + rt.ty[gi] * hcos;
  let load = G * cosSlope + car.vx * car.vx * bend;
  let slopeFx = -m * G * sinSlope * along;
  let slopeFy = -m * G * sinSlope * across;
  // --- banking (v3 circuits): gravity pulls the car down the bank, and turning into it presses the car onto the road.
  const bank = rt.bank[gi];
  if (bank !== 0) {
    const cb = 1 / Math.sqrt(1 + bank * bank);
    const sb = bank * cb;
    load = G * cosSlope * cb + car.vx * car.vx * (bend - rt.curv[gi] * sb);
    // Down the bank is toward the right of the road when its left side is higher.
    const nf = -rt.ty[gi] * hcos + rt.tx[gi] * hsin;
    const nl = rt.ty[gi] * hsin + rt.tx[gi] * hcos;
    slopeFx -= m * G * sb * nf;
    slopeFy -= m * G * sb * nl;
  }
  const gEff = clamp(load, G * 0.45, G * 2);

  // --- integrate
  const substeps = p.substeps;
  const h = dt / substeps;
  const Fz = m * gEff;
  const minLoad = 0.12 * Fz;
  let spinAcc = 0;
  let lockAcc = 0;
  let rpmOut = p.idleRpm;
  const ratio = gearRatio(p, car.reverse ? 1 : car.gear) * p.finalDrive;

  for (let k = 0; k < substeps; k++) {
    const vx = car.vx;
    const vy = car.vy;
    const r = car.yawRate;
    const spd = Math.sqrt(vx * vx + vy * vy);

    // Axle loads with longitudinal weight transfer, plus downforce.
    const transfer = (m * car.ax * p.cgHeight) / L;
    let Fzf = (Fz * p.cgToRear) / L - transfer;
    if (Fzf < minLoad) Fzf = minLoad;
    if (Fzf > Fz - minLoad) Fzf = Fz - minLoad;
    // Ground effect needs the car pointing where it's going (cars with aeroYawLoss): sideways, the floor loses its
    // seal and the downforce goes with it (a fifth of it at 13 degrees, half at 20 for the 2022 car).
    let aero = 1;
    if (p.aeroYawLoss) {
      const u = (vx < 0 ? -vx : vx) > 1 ? vy / vx : 0;
      aero = 1 - p.aeroYawLoss * u * u;
      if (aero < 0.2) aero = 0.2;
    }
    const v2 = p.aeroYawLoss ? vx * vx * aero : vx * vx;
    const Fzr = Fz - Fzf + p.downforceRear * v2;
    Fzf += p.downforceFront * v2;
    const capF = p.tireMu * p.frontGrip * gripF * Fzf;
    const capR = p.tireMu * p.rearGrip * gripR * Fzr;

    // Engine through the current gear.
    const wheelRad = (vx < 0 ? -vx : vx) / p.rearWheelRadius;
    let rpm = wheelRad * ratio * RPM_PER_RAD_S;
    if (rpm < p.idleRpm) rpm = p.idleRpm;
    const torque = rpm >= p.redlineRpm ? 0 : p.enginePeakTorque * torqueFactor(rpm / p.redlineRpm);
    let Fdrive = (drivePedal * torque * ratio * p.drivetrainEfficiency) / p.rearWheelRadius;
    if (car.reverse) {
      Fdrive = vx < -7 ? 0 : -(Fdrive < 2600 ? Fdrive : 2600);
    }
    rpmOut = rpm;

    // Brakes oppose the rolling direction and never push the car backwards.
    let Fbf = brakePedal * p.brakeForce * p.brakeBias;
    let Fbr = brakePedal * p.brakeForce * (1 - p.brakeBias);
    const hold = ((vx < 0 ? -vx : vx) * m) / h;
    if (Fbf + Fbr > hold) {
      const sc = hold / (Fbf + Fbr);
      Fbf *= sc;
      Fbr *= sc;
    }
    const bdir = vx > 0 ? -1 : 1;
    let Fxf = bdir * Fbf;
    let Fxr = Fdrive + bdir * Fbr;

    // Slip angles in each axle's own frame.
    const vfx = vx;
    const vfy = vy + p.cgToFront * r;
    const vfLong = cd * vfx + sd * vfy;
    const vfLat = -sd * vfx + cd * vfy;
    const vrLat = vy - p.cgToRear * r;
    const aF = datan2(vfLat, Math.max(vfLong < 0 ? -vfLong : vfLong, 0.5));
    const aR = datan2(vrLat, Math.max(vx < 0 ? -vx : vx, 0.5));

    // Friction ellipse: longitudinal demand first, lateral gets what's left.
    const capFx = capF * p.frictionEllipse;
    const capRx = capR * p.frictionEllipse;
    // Driver aids: traction control trims the throttle before the rears spin up
    // (and, mid-corner, before throttle steals the grip the rear needs to hold
    // the line); ABS trims the brakes before a wheel locks.
    if (p.tractionControl > 0 && Fdrive > 0) {
      // Slow corners still let the rear slide on too much throttle; fast ones hold the line.
      const fast = clamp((spd - 15) / 20, 0, 1);
      const uLat = tireShape((aR < 0 ? -aR : aR) / p.peakSlipRear, p.falloffRear) * p.tcCornering * fast;
      const cap = capRx * p.tcTarget * Math.sqrt(1 - uLat * uLat > 0 ? 1 - uLat * uLat : 0);
      if (Fdrive > cap) {
        Fdrive = Fdrive + (cap - Fdrive) * p.tractionControl;
        Fxr = Fdrive + bdir * Fbr;
      }
    }
    if (p.abs > 0) {
      // Steering-aware ABS (cars from HANDLING_3_FROM): the more steering you're holding, the less brake
      // pressure it lets through, on both axles so the balance stays put, leaving the tyres grip to turn with:
      // braking into a corner still turns. Without it, exactly as before.
      if (p.absCornering) {
        // (From handling 3.1: only at the speeds where braking ploughed on, fading out above them, so braking
        // hard from speed with the steering held still stops the car as before.)
        const fade = p.absCorneringTop ? clamp((p.absCorneringTop - spd) / 15, 0, 1) : 1;
        const u = (car.steer < 0 ? -car.steer : car.steer) * p.absCornering * fade;
        const k = Math.sqrt(1 - u * u);
        Fbf *= k;
        Fbr *= k;
        Fxf = bdir * Fbf;
        Fxr = Fdrive + bdir * Fbr;
      }
      let lf = capFx * p.absTarget;
      let lr = capRx * p.absTarget;
      if (p.absSliding) {
        // (Cars with absSliding.) A tyre past its peak slip angle is sliding sideways: its brake gives way to the grip
        // it needs to hold the car, down to absSlidingFloor of it. Braking at full strength along a nose that points
        // into the corner, the tyres shoved the car out the other way.
        let qf = ((aF < 0 ? -aF : aF) / p.peakSlipFront - 1) * p.absSliding;
        let qr = ((aR < 0 ? -aR : aR) / p.peakSlipRear - 1) * p.absSliding;
        qf = qf < 0 ? 0 : qf > 1 ? 1 : qf;
        qr = qr < 0 ? 0 : qr > 1 ? 1 : qr;
        const fl = p.absSlidingFloor ?? 0;
        lf *= 1 - (1 - fl) * qf;
        lr *= 1 - (1 - fl) * qr;
      }
      if (Fbf > lf) {
        Fbf = Fbf + (lf - Fbf) * p.abs;
        Fxf = bdir * Fbf;
      }
      if (Fbr > lr && Fdrive < 1) {
        Fbr = Fbr + (lr - Fbr) * p.abs;
        Fxr = Fdrive + bdir * Fbr;
      }
    }
    let lockedF = false;
    let lockedR = false;
    if ((Fxf < 0 ? -Fxf : Fxf) > capFx) {
      Fxf = (Fxf < 0 ? -capFx : capFx) * p.lockGrip;
      lockedF = true;
      lockAcc += 0.5;
    }
    if ((Fxr < 0 ? -Fxr : Fxr) > capRx) {
      const driving = (Fdrive < 0 ? -Fdrive : Fdrive) > Fbr;
      Fxr = (Fxr < 0 ? -capRx : capRx) * (driving ? p.wheelspinGrip : p.lockGrip);
      if (driving) spinAcc += 1;
      else {
        lockedR = true;
        lockAcc += 0.5;
      }
    }
    const uf = Fxf / capFx;
    const ur = Fxr / capRx;
    const latF = lockedF ? capF * p.lockLateral : capF * Math.sqrt(1 - uf * uf > 0 ? 1 - uf * uf : 0);
    const latR = lockedR ? capR * p.lockLateral : capR * Math.sqrt(1 - ur * ur > 0 ? 1 - ur * ur : 0);

    const Fyf = -latF * tireShape((aF < 0 ? -aF : aF) / p.peakSlipFront, p.falloffFront) * (aF < 0 ? -1 : aF > 0 ? 1 : 0);
    const Fyr = -latR * tireShape((aR < 0 ? -aR : aR) / p.peakSlipRear, p.falloffRear) * (aR < 0 ? -1 : aR > 0 ? 1 : 0);

    // Body-frame totals.
    const frontLat = sd * Fxf + cd * Fyf;
    let Fx = Fxr + cd * Fxf - sd * Fyf;
    let Fy = Fyr + frontLat;
    const yawTorque = p.cgToFront * frontLat - p.cgToRear * Fyr;
    const drag = p.dragCoef * spd;
    Fx -= drag * vx + p.rollingResistance * vx;
    Fy -= drag * vy;
    Fx += slopeFx;
    Fy += slopeFy;
    if (dragAcc > 0) {
      const kd = (m * dragAcc) / (spd > 1 ? spd : 1);
      Fx -= kd * vx;
      Fy -= kd * vy;
    }

    const axF = Fx / m;
    const ayF = Fy / m;
    let yawAcc = yawTorque / p.yawInertia;
    let esc = 0;
    // Stability assist: if the car rotates faster than the steering asks for
    // (or than the tires could sustain), damp the excess, so a slide recovers
    // on its own instead of becoming a spin. Understeer is left alone.
    if (p.yawAssist > 0 && vx > 2) {
      const rKin = (vx * (sd / cd)) / L;
      const muEff = p.tireMu * (p.frontGrip * gripF < p.rearGrip * gripR ? p.frontGrip * gripF : p.rearGrip * gripR);
      // What the tyres can sustain includes downforce (the weaker axle's share), or winged cars can't use it.
      const aeroF = (p.downforceFront * L) / (m * p.cgToRear);
      const aeroR = (p.downforceRear * L) / (m * p.cgToFront);
      const vv = spd > 3 ? spd : 3;
      const rMax = (muEff * (gEff + (aeroF < aeroR ? aeroF : aeroR) * (p.aeroYawLoss ? vv * vv * aero : vv * vv))) / vv;
      const rT = rKin > rMax ? rMax : rKin < -rMax ? -rMax : rKin;
      const over = r - rT;
      if (over * r > 0 && (r < 0 ? -r : r) > (rT < 0 ? -rT : rT)) {
        yawAcc -= p.yawAssist * over;
        // (Cars with escBrake.) As a real stability control does, it straightens the car by braking a wheel: a slide
        // it catches costs a little speed (never more than escMaxDecel), instead of being caught for free.
        if (p.escBrake) {
          const d = p.escBrake * (over < 0 ? -over : over);
          esc = d < (p.escMaxDecel ?? d) ? d : (p.escMaxDecel ?? d);
        }
      }
    }
    car.vx = esc ? vx + (axF - (vx > 0 ? esc : -esc) + vy * r) * h : vx + (axF + vy * r) * h;
    car.vy = vy + (ayF - vx * r) * h;
    car.yawRate = r + yawAcc * h;
    // Slip limiter: never let the car go fully sideways.
    if (p.slipAssistAngle > 0 && car.vx > 2) {
      // (Cars with slipAssistBraking.) Braking, it holds the car straighter, and sooner: with the weight on the nose
      // the rear steps out, and brakes pushing along a nose that points into the corner shove the car the other way.
      const lim = p.slipAssistBraking ? car.vx * (p.slipAssistAngle - (p.slipAssistAngle - p.slipAssistBraking) * brakePedal) : car.vx * p.slipAssistAngle;
      if (p.slipAssistStraighten) {
        // (Cars with slipAssistStraighten.) The slide is trimmed by turning the nose towards where the car is going,
        // at the same speed: trimming the sideways speed instead bends the path round the corner for nothing.
        const over = car.vy > lim ? car.vy - lim : car.vy < -lim ? car.vy + lim : 0;
        if (over !== 0) {
          const vy1 = car.vy - over * ((p.slipAssistBraking ? 16 : 8) * h);
          const vx1 = Math.sqrt(car.vx * car.vx + car.vy * car.vy - vy1 * vy1);
          car.heading += datan2(car.vy, car.vx) - datan2(vy1, vx1);
          car.vx = vx1;
          car.vy = vy1;
        }
      } else if (car.vy > lim) car.vy -= (car.vy - lim) * (8 * h);
      else if (car.vy < -lim) car.vy -= (car.vy + lim) * (8 * h);
    }

    // Below a few m/s the tire model is ill-conditioned: blend to kinematic steering.
    const blend = clamp((spd - 0.3) / p.lowSpeedBlend, 0, 1);
    if (blend < 1) {
      const rKin = (car.vx * (sd / cd)) / L;
      car.yawRate = rKin + (car.yawRate - rKin) * blend;
      car.vy *= blend;
    }

    car.heading += car.yawRate * h;
    const ch = dcos(car.heading);
    const sh = dsin(car.heading);
    car.x += (car.vx * ch - car.vy * sh) * h;
    car.y += (car.vx * sh + car.vy * ch) * h;
    car.ax = axF;
    car.ay = ayF;
    car.slipFront = aF;
    car.slipRear = aR;
  }
  car.heading = wrapAngle(car.heading);
  car.wheelspin = spinAcc / substeps;
  car.lockup = lockAcc / substeps;
  car.odometer += Math.sqrt(car.vx * car.vx + car.vy * car.vy) * dt;

  // --- automatic gearbox
  if (car.reverse === 0) {
    const rpmNow = ((car.vx < 0 ? -car.vx : car.vx) / p.rearWheelRadius) * ratio * RPM_PER_RAD_S;
    if (rpmNow > p.shiftUpRpm && car.gear < MAX_GEAR) car.gear++;
    else if (rpmNow < p.shiftDownRpm && car.gear > 1) car.gear--;
  }
  // Audible rpm: wheelspin and a slipping clutch at low speed rev the engine up.
  const launch = speed0 < 12 ? (1 - speed0 / 12) * throttleIn * 0.45 * p.redlineRpm : 0;
  let rpmAudio = rpmOut * (1 + car.wheelspin * 0.22);
  if (rpmAudio < p.idleRpm + launch) rpmAudio = p.idleRpm + launch;
  car.rpm = rpmAudio > p.redlineRpm ? p.redlineRpm : rpmAudio;

  // --- where are we on the circuit; barriers at the edge of the runoff
  projectLocal(rt, car.x, car.y, car.trackIndex, 12, proj);
  car.trackIndex = proj.index;
  car.s = proj.s;
  car.d = proj.d;
  car.barrierImpact = 0;
  const limit = barrierAt(rt, proj) - p.carHalfWidth;
  const ad = proj.d < 0 ? -proj.d : proj.d;
  if (ad > limit) {
    const side = proj.d > 0 ? 1 : -1;
    const nx = -proj.ty * side;
    const ny = proj.tx * side;
    const pen = ad - limit;
    car.x -= nx * pen;
    car.y -= ny * pen;
    car.d = side * limit;
    const ch = dcos(car.heading);
    const sh = dsin(car.heading);
    let Vx = car.vx * ch - car.vy * sh;
    let Vy = car.vx * sh + car.vy * ch;
    const vn = Vx * nx + Vy * ny;
    if (vn > 0) {
      const e = p.barrierRestitution;
      Vx -= (1 + e) * vn * nx;
      Vy -= (1 + e) * vn * ny;
      const tx = -ny;
      const ty = nx;
      const vt = Vx * tx + Vy * ty;
      const avt = vt < 0 ? -vt : vt;
      let dv = p.barrierFriction * (1 + e) * vn;
      if (dv > avt) dv = avt;
      const st = vt < 0 ? -1 : 1;
      Vx -= st * dv * tx;
      Vy -= st * dv * ty;
      car.vx = Vx * ch + Vy * sh;
      car.vy = -Vx * sh + Vy * ch;
      car.yawRate *= 0.6;
      car.barrierImpact = vn;
    }
  }
}

/** Place the car on the centerline at arc length s, rolling at `speed`. */
/** Put the car on the track at arc length s (and `offset` metres left of the centreline), rolling at `speed`. */
export function placeCar(car: CarState, rt: TrackRuntime, p: PhysicsTuning, s: number, speed: number, offset = 0): void {
  let ss = s % rt.length;
  if (ss < 0) ss += rt.length;
  let i = 0;
  while (i + 1 < rt.n && rt.s[i + 1] <= ss) i++;
  const t = rt.segLen[i] > 0 ? (ss - rt.s[i]) / rt.segLen[i] : 0;
  let x = rt.px[i] + rt.segDx[i] * t;
  let y = rt.py[i] + rt.segDy[i] * t;
  if (offset !== 0 && rt.segLen[i] > 0) {
    x -= (rt.segDy[i] / rt.segLen[i]) * offset;
    y += (rt.segDx[i] / rt.segLen[i]) * offset;
  }
  const heading = datan2(rt.segDy[i], rt.segDx[i]);
  const fresh = createCar();
  Object.assign(car, fresh);
  car.x = x;
  car.y = y;
  car.heading = heading;
  car.vx = speed;
  car.gear = gearForSpeed(p, speed);
  car.rpm = p.idleRpm;
  car.trackIndex = i;
  car.s = ss;
  car.d = offset;
}
